/**
 * The Related items switcher's pure parts (KPR-719, epic KPE-029): which
 * entries fit, how a count reads, and what an empty list says.
 *
 * The first entry is always `Related items` (not stored, never renamed: the
 * API reserves the name); each related list the card shows follows it.
 */

import { serialize } from './kql';
import { parseForBuilder, withSearchTerm } from './kqlBuilder';

/** The id of the first entry. A list id is 10 characters of [a-z0-9], so it never collides. */
export const RELATED_ITEMS_ENTRY_ID = 'related-items';
export const RELATED_ITEMS_NAME = 'Related items';

const COUNT_FORMAT = typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat() : null;

/** 12345 -> "12,345". A count that is not a number reads as nothing. */
export function formatListCount(count) {
	const n = Number(count);
	if (count === null || count === undefined || count === '' || !Number.isFinite(n)) return '';
	return COUNT_FORMAT ? COUNT_FORMAT.format(n) : String(n);
}

/** What a screen reader adds after a name: ", 12 items". */
export function spokenCount(count) {
	const text = formatListCount(count);
	if (!text) return '';
	return `${text} ${Number(count) === 1 ? 'item' : 'items'}`;
}

/**
 * Which entries are drawn and which wait in More.
 *
 *   fitEntries({ ids, selectedId, widths, moreWidth, available, gap })
 *     -> { visible: [ids...], overflow: [ids...] }
 *
 * - `widths` is each entry's own width in the same order as `ids`; `available`
 *   the room the switcher has; `moreWidth` the More button's, drawn whenever
 *   something overflows.
 * - As many entries as fit, in order, and at least one. When nothing is known
 *   (`available` or a width is 0, as before layout or without a layout
 *   engine) everything is drawn: more than fits is the safe error, a missing
 *   entry is not.
 * - The selected entry is always drawn. One that would have been in More takes
 *   the place of the entries that no longer fit beside it, after the others
 *   (order is kept), and moves out again when another is selected.
 */
export function fitEntries({ ids, selectedId, widths, moreWidth = 0, available, gap = 0 }) {
	const total = ids.length;
	const all = { visible: [...ids], overflow: [] };
	if (total <= 1) return all;
	const known = available > 0 && widths.length === total && widths.every(width => width > 0);
	if (!known) return all;

	const sum = (count) => widths.slice(0, count).reduce((acc, width) => acc + width + gap, -gap * Math.min(count, 1));
	const room = available;
	if (sum(total) <= room) return all;
	let count = 0;
	while (count < total && sum(count + 1) + (count + 1 < total ? gap + moreWidth : 0) <= room) count += 1;
	if (count >= total) return all;

	const selectedIndex = Math.max(0, ids.indexOf(selectedId));
	if (selectedIndex < Math.max(count, 1)) {
		const shown = Math.max(count, 1);
		return { visible: ids.slice(0, shown), overflow: ids.slice(shown) };
	}

	// The selected entry is beyond the cut: it takes its place after as many of
	// the leading entries as fit beside it and the More button.
	const selectedWidth = widths[selectedIndex];
	let lead = Math.min(count, selectedIndex);
	const needed = (leading) => (leading ? sum(leading) + gap : 0) + selectedWidth + gap + moreWidth;
	while (lead > 0 && needed(lead) > room) lead -= 1;
	const visibleIds = [...ids.slice(0, lead), ids[selectedIndex]];
	return { visible: visibleIds, overflow: ids.filter(id => !visibleIds.includes(id)) };
}

/**
 * Why a list that works shows nothing, from the API's `X-Related-List-State`
 * (the `state` of the list in the switcher's data). The sentence says what is
 * wrong and who can fix it; none of them is an error the viewer caused.
 */
const STATE_SENTENCES = Object.freeze({
	removed: 'This list’s lookup was removed, so it can’t find items. A manager can update the list in the card’s settings.',
	not_a_lookup: 'This list’s element is no longer a lookup, so it can’t find items. A manager can update the list in the card’s settings.',
	points_elsewhere: 'This list’s lookup now points at a different card, so it can’t find items. A manager can update the list in the card’s settings.',
	no_lookup: 'No lookup points at this card any more, so this list can’t find items. A manager can update the list in the card’s settings.',
	source_removed: 'This list’s source card was removed, so it can’t find items. A manager can update the list in the card’s settings.',
	filter_unreadable: 'This list’s default filter can’t be read, so it shows nothing. A manager can fix it in the card’s settings.',
});

export function listStateSentence(state) {
	if (!state) return null;
	return STATE_SENTENCES[state] || 'This list can’t find items right now. A manager can check it in the card’s settings.';
}


/**
 * A query text with the search box's term taken off (every top-level
 * `text ~ …`); anything else in it is kept. A text that does not parse is
 * returned as it is: the box never writes one.
 */
export function withoutSearchTerm(query) {
	const text = `${query || ''}`.trim();
	if (!text) return '';
	const { ast, error } = parseForBuilder(text);
	if (error || !ast) return text;
	const rest = withSearchTerm(ast, '', 'text');
	return rest ? serialize(rest) : '';
}

/**
 * What of a related list's view state may be SAVED to the account (KPR-722):
 * the viewer's added filter, sort and grid/table - and never the search term
 * (owner ruling t03, KPE-029). A search is for the moment it is typed; coming
 * back to a list tomorrow to find it narrowed by a word forgotten is a list
 * that seems to be missing items. The open list keeps its term in the query
 * text while it is on screen, so whatever saves the state saves THIS:
 *
 *   save(savableViewState(viewState))
 *
 * The account save (useRelatedListAccountState, KPR-722) takes its filter and
 * grid/table from this; it keeps the sort in the session only.
 */
export function savableViewState(state) {
	if (!state || typeof state !== 'object') return state;
	if (state.query === undefined || state.query === null) return state;
	return { ...state, query: withoutSearchTerm(state.query) };
}
